import { useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@/fontawesome';
import { useDayTradingWebSocket } from '@/hooks/useDayTradingWebSocket';
import { useDarkMode } from '@/hooks/useDarkMode';
import { useSoundMuted } from '@/hooks/useSoundMuted';
import { useUser } from '@/contexts/UserContext';
import ChatPanel from '@/components/daytrading/components/ChatPanel';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import ConnectionStatusIndicator from '@/components/daytrading/components/ConnectionStatusIndicator';
import { DAY_TRADING_ROUTES } from '@/constants/routes';
import type { DayTradingSessionID, UserID } from '@/models/ids';
import type { DayTradingSessionChat } from '@/models/day-trading-session';

type Role = 'moderator' | 'participant' | 'observer';

function parseRole(raw: string | null): Role {
  if (raw === 'moderator' || raw === 'observer') return raw;
  return 'participant';
}

export default function DayTradingChatStandalonePage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [searchParams] = useSearchParams();
  const dtsid = sessionId as DayTradingSessionID;
  const role = parseRole(searchParams.get('role'));

  const { user } = useUser();
  const { isMuted } = useSoundMuted();
  useDarkMode();

  const {
    initialLoading,
    initialError,
    connectionStatus,
    sessionDeleted,
    chat,
    setChat,
  } = useDayTradingWebSocket({ sessionId: dtsid, role });

  const handleChatUpdate = useCallback(
    (next: DayTradingSessionChat) => {
      setChat(next);
    },
    [setChat],
  );

  if (initialLoading) {
    return <LoadingSpinner message="Lade Chat..." />;
  }

  if (initialError !== null) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-600 dark:text-red-400 mb-4">{initialError}</p>
        <Link to={DAY_TRADING_ROUTES.LIST} className="text-blue-600 dark:text-blue-400 hover:underline">
          <FontAwesomeIcon icon={faArrowLeft} className="w-4 h-4 mr-2" />
          Zurück zur Übersicht
        </Link>
      </div>
    );
  }

  if (sessionDeleted) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-600 dark:text-red-400">Diese Session wurde gelöscht.</p>
      </div>
    );
  }

  if (user === null || chat === null) {
    return <LoadingSpinner message="Lade Chat..." />;
  }

  return (
    <div className="fixed inset-0 flex flex-col bg-gray-50 dark:bg-gray-900">
      <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-white dark:bg-gray-800">
        <span className="text-sm font-semibold text-gray-900 dark:text-white">Daytrading Chat</span>
        <ConnectionStatusIndicator status={connectionStatus} />
      </div>
      <div className="flex-1 min-h-0 p-2">
        <ChatPanel
          chat={chat}
          sessionId={dtsid}
          currentUserId={user.id as UserID}
          canWrite={role !== 'observer'}
          canDelete={user.permissionGroup === 'EMPLOYEE' || user.permissionGroup === 'ADMIN'}
          isMuted={isMuted}
          onChatUpdate={handleChatUpdate}
          standalone
        />
      </div>
    </div>
  );
}
